import React, { useEffect } from 'react';
import { Formik, Form } from 'formik';
import { FormElement, Label } from '@dashboard/components/Form';
import { isEmpty } from 'lodash';
import FormSubmit from '@dashboard/components/Formik/components/FormSubmit';
import { transformApiErrorToFormik } from '@dashboard/services/form';
import { usePreview } from '@dashboard/context/PreviewContext';
import { Checkbox, Radio } from '@dashboard/components/Formik';
import { AfterPaymentActionEnum } from '@dashboard/enums/after-payment';
import { InfoTooltip } from '@dashboard/components/Tooltip';
import { PAGE_TYPES } from '@dashboard/enums/page-types';
import usePage from '@/dashboard/hooks/usePage';
import { UnsavedChangesReporter } from '@dashboard/components/UnsavedChanges';

const toDownloadsValue = page =>
  page.showFilesList
    ? 'showFilesList'
    : page.showViewDownloadsButton
      ? 'showViewDownloadsButton'
      : '';

const DefaultConfirmationMessageForm = () => {
  const { page, updatePage } = usePage();

  const [, previewDispatch] = usePreview();
  const setPreview = value => previewDispatch({ type: 'set', value });

  const checkoutValues = {
    downloads: toDownloadsValue(page),
    afterPaymentAction: page.afterPaymentAction,
    customizeCheckoutConfirmation: page.customizeCheckoutConfirmation,
    showConfirmationEventName: page.showConfirmationEventName,
    showConfirmationEventDateTime: page.showConfirmationEventDateTime,
    showConfirmationEventLocation: page.showConfirmationEventLocation,
    showConfirmationEventMeetingLink: page.showConfirmationEventMeetingLink,
    showConfirmationEventTickets: page.showConfirmationEventTickets,
    showConfirmationAddToCalendar: page.showConfirmationAddToCalendar,
    showConfirmationEventAddOns: page.showConfirmationEventAddOns,
  };

  return (
    <Formik
      initialValues={checkoutValues}
      onSubmit={async (values, actions) => {
        actions.setStatus(undefined);

        const { downloads, ...rest } = values;
        const payload = {
          ...rest,
          showFilesList: downloads === 'showFilesList',
          showViewDownloadsButton: downloads === 'showViewDownloadsButton',
        };

        const { data, error } = await updatePage.mutateAsync(payload);

        if (data) {
          if (data.afterPaymentAction === AfterPaymentActionEnum.CONFIRMATION) {
            setPreview({
              type: 'confirmationCheckoutMessage',
            });
          }

          actions.setStatus('');
          actions.resetForm({
            values: {
              downloads: toDownloadsValue(data),
              afterPaymentAction: data.afterPaymentAction,
              customizeCheckoutConfirmation: data.customizeCheckoutConfirmation,
              showConfirmationEventName: data.showConfirmationEventName,
              showConfirmationEventDateTime: data.showConfirmationEventDateTime,
              showConfirmationEventLocation: data.showConfirmationEventLocation,
              showConfirmationEventMeetingLink: data.showConfirmationEventMeetingLink,
              showConfirmationEventTickets: data.showConfirmationEventTickets,
              showConfirmationAddToCalendar: data.showConfirmationAddToCalendar,
              showConfirmationEventAddOns: data.showConfirmationEventAddOns,
            },
          });
        } else {
          const { status, errors } = transformApiErrorToFormik(error);

          if (!isEmpty(errors)) {
            actions.setErrors(errors);
          } else {
            actions.setStatus(status);
          }
        }
      }}
    >
      {({ dirty, setFieldValue, handleSubmit, isSubmitting }) => {
        useEffect(() => {
          setFieldValue('afterPaymentAction', AfterPaymentActionEnum.CONFIRMATION);
          setFieldValue('customizeCheckoutConfirmation', false);
        }, []);

        return (
          <Form onSubmit={handleSubmit}>
            <UnsavedChangesReporter />
            {(page.type === PAGE_TYPES.CHECKOUT || page.type === PAGE_TYPES.FORM) && (
              <FormElement>
                <Label htmlFor="downloads">
                  Show on confirmation{' '}
                  <InfoTooltip>
                    If you have files attached to this checkout, you can choose to display them as a
                    list or have a button that links to the files.
                  </InfoTooltip>
                </Label>

                <Radio.Group name="downloads">
                  <Radio.Wrapper>
                    <Radio.Input value="showFilesList" id="showFilesList" />
                    <Radio.Label htmlFor="showFilesList">List of files</Radio.Label>
                  </Radio.Wrapper>

                  <Radio.Wrapper>
                    <Radio.Input value="showViewDownloadsButton" id="showViewDownloadsButton" />
                    <Radio.Label htmlFor="showViewDownloadsButton">
                      "View downloads" button
                    </Radio.Label>
                  </Radio.Wrapper>
                </Radio.Group>
              </FormElement>
            )}

            {page.type === PAGE_TYPES.EVENT && (
              <>
                <FormElement>
                  <Label>Show event details</Label>
                  <Checkbox.Group>
                    <Checkbox.Wrapper>
                      <Checkbox id="showConfirmationEventName" name="showConfirmationEventName" />
                      <Checkbox.Label htmlFor="showConfirmationEventName">
                        Event name
                      </Checkbox.Label>
                    </Checkbox.Wrapper>
                    <Checkbox.Wrapper>
                      <Checkbox
                        id="showConfirmationEventDateTime"
                        name="showConfirmationEventDateTime"
                      />
                      <Checkbox.Label htmlFor="showConfirmationEventDateTime">
                        Date and time
                      </Checkbox.Label>
                    </Checkbox.Wrapper>
                    <Checkbox.Wrapper>
                      <Checkbox
                        id="showConfirmationEventLocation"
                        name="showConfirmationEventLocation"
                      />
                      <Checkbox.Label htmlFor="showConfirmationEventLocation">
                        Location
                      </Checkbox.Label>
                    </Checkbox.Wrapper>

                    <Checkbox.Wrapper>
                      <Checkbox
                        id="showConfirmationEventMeetingLink"
                        name="showConfirmationEventMeetingLink"
                      />
                      <Checkbox.Label htmlFor="showConfirmationEventMeetingLink">
                        {page.eventDetails?.type === 'hybrid' ? 'Hybrid event' : 'Virtual event'}
                      </Checkbox.Label>
                    </Checkbox.Wrapper>
                  </Checkbox.Group>
                </FormElement>

                <FormElement>
                  <Label>Show booking details</Label>

                  <Checkbox.Group>
                    <Checkbox.Wrapper>
                      <Checkbox
                        id="showConfirmationEventTickets"
                        name="showConfirmationEventTickets"
                      />
                      <Checkbox.Label htmlFor="showConfirmationEventTickets">
                        Tickets purchased
                      </Checkbox.Label>
                    </Checkbox.Wrapper>

                    <Checkbox.Wrapper>
                      <Checkbox
                        id="showConfirmationEventAddOns"
                        name="showConfirmationEventAddOns"
                      />
                      <Checkbox.Label htmlFor="showConfirmationEventAddOns">Add-ons</Checkbox.Label>
                    </Checkbox.Wrapper>

                    <Checkbox.Wrapper>
                      <Checkbox
                        id="showConfirmationAddToCalendar"
                        name="showConfirmationAddToCalendar"
                      />
                      <Checkbox.Label htmlFor="showConfirmationAddToCalendar">
                        Add to calendar
                      </Checkbox.Label>
                    </Checkbox.Wrapper>
                  </Checkbox.Group>
                </FormElement>
              </>
            )}

            <FormSubmit>
              <FormSubmit.Button>Save</FormSubmit.Button>
              <FormSubmit.State visible={!!dirty && !isSubmitting} />
            </FormSubmit>
          </Form>
        );
      }}
    </Formik>
  );
};

export default DefaultConfirmationMessageForm;
